import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  deleteSchedule,
  fetchDeviceDetails,
  fetchFlowRate,
  fetchSchedule,
  fetchStopTimer,
  saveSchedule,
  startStopTimer,
} from './details-api';
import type { DeviceSchedule, FlowRateRange } from './details-schemas';
import { deviceKeys } from './hooks';

export const detailKeys = {
  detail: (id: string) => [...deviceKeys.all, 'detail', id] as const,
  flowRate: (id: string, range: FlowRateRange) =>
    [...deviceKeys.all, 'flow-rate', id, range] as const,
  schedule: (id: string) => [...deviceKeys.all, 'schedule', id] as const,
  stopTimer: (id: string) => [...deviceKeys.all, 'stop-timer', id] as const,
};

/**
 * The Device Details payload.
 *
 * Unlike the listing (§14 forbids background updates there), the details page's metrics are
 * expected to refresh when the device reports (US-112437 §2), so this keeps the app-wide
 * focus-refetch behaviour rather than opting out.
 */
export function useDeviceDetails(deviceId: string) {
  return useQuery({
    queryKey: detailKeys.detail(deviceId),
    queryFn: () => fetchDeviceDetails(deviceId),
    enabled: Boolean(deviceId),
  });
}

/**
 * Flow-rate history for one range. Keyed by `(deviceId, range)` so switching range refetches —
 * aggregation is server-side per range, not a client-side re-slice of one dataset.
 *
 * Its own error is deliberately *not* fatal to the page: the card shows a retryable error state
 * while every other section keeps rendering (§13 "Partial load failure").
 */
export function useFlowRate(deviceId: string, range: FlowRateRange) {
  return useQuery({
    queryKey: detailKeys.flowRate(deviceId, range),
    queryFn: () => fetchFlowRate({ deviceId, range }),
    enabled: Boolean(deviceId),
  });
}

/**
 * The device's schedule. `null` data means "no schedule configured" — a 200 answer, not a
 * failure — so the drawer opens empty rather than showing an error.
 */
export function useDeviceSchedule(deviceId: string, enabled = true) {
  return useQuery({
    queryKey: detailKeys.schedule(deviceId),
    queryFn: () => fetchSchedule(deviceId),
    enabled: enabled && Boolean(deviceId),
  });
}

export function useSaveSchedule(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (schedule: DeviceSchedule) => saveSchedule({ deviceId, schedule }),
    meta: { successMessage: 'Schedule saved successfully' },
    onSuccess: (saved) => {
      queryClient.setQueryData(detailKeys.schedule(deviceId), saved);
      // `controls.scheduleConfigured` on the details payload changes too.
      void queryClient.invalidateQueries({ queryKey: detailKeys.detail(deviceId) });
    },
  });
}

export function useDeleteSchedule(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deleteSchedule(deviceId),
    meta: { successMessage: 'Schedule removed' },
    onSuccess: () => {
      queryClient.setQueryData(detailKeys.schedule(deviceId), null);
      void queryClient.invalidateQueries({ queryKey: detailKeys.detail(deviceId) });
    },
  });
}

/**
 * Re-read just the stop timer. Called when the countdown reaches zero: only the server knows
 * whether the timer actually fired, and re-reading one timer is cheaper than the whole device.
 * Disabled by default — the details payload already reports the timer on load.
 */
export function useStopTimer(deviceId: string, enabled = false) {
  return useQuery({
    queryKey: detailKeys.stopTimer(deviceId),
    queryFn: () => fetchStopTimer(deviceId),
    enabled: enabled && Boolean(deviceId),
  });
}

export function useStartStopTimer(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (durationMinutes: number) => startStopTimer({ deviceId, durationMinutes }),
    meta: { successMessage: 'Stop timer set successfully' },
    onSuccess: (timer) => {
      queryClient.setQueryData(detailKeys.stopTimer(deviceId), timer);
      void queryClient.invalidateQueries({ queryKey: detailKeys.detail(deviceId) });
    },
  });
}
